import * as React from "react";
import * as ReactRedux from "react-redux";
import {
    BrowserRouter,
    Redirect,
    Route,
    Switch,
    useLocation,
} from "react-router-dom";

import { RootState, commonSlice, store } from "./store";

import { LoginContainer, LoginOTPContainer } from "./features/Login/Login";
import { TripsMap } from "./features/ShiftMonitoring/Trips/TripsMap";
import { TripsScreen } from "./features/ShiftMonitoring/Trips/TripsScreen";
import { Container } from "./layout";

import { BreaksScreen } from "./features/ShiftMonitoring/Breaks/BreaksScreen";
import { SearchScreen } from "./features/ShiftMonitoring/ShiftSelect/SearchScreen";

import { ReserveTripsMap } from "./features/Reserve/Map";
import { Container as ReserveShiftsContainer } from "./features/Reserve/Shifts/Container";
import { Container as ReserveTripsContainer } from "./features/Reserve/Trips/Container";

import { DriversListContainer } from "./features/DriversListMenu";
import { ManualContainer } from "./features/Manual";
import { ReleaseNoteContainer } from "./features/RelaseNote";
import { CapacityContainer } from "./features/UsageBased_Capacity";
import { BasePaymentsRouter } from "./features/UsageBased_Summary";
import { BillingSummaryByDriverContainer } from "./features/UsageBased_Summary/BillingSummaryByDriver";
import { BilledTripsDetailsContainer } from "./features/UsageBased_Trips/BilledTripsDetails";
import { WeeklyReportsContainer } from "./features/WeeklyReport";
import { WeeklyDetailScreenContainer } from "./features/WeeklyReport/DetailScreen";

import {
    Session,
    getSessionCookie,
    isLastLoginOtp,
    isLoggedIn,
} from "./components/cookies";
import { api } from "./services/common/make_api";
import { setupStore } from "./services/login/login";
// TODO: 各componentが受け取るpropsを確認

export const BaseRouter: React.FC<RootState> = (props: RootState) => {
    // 別タブや別windowに遷移した時に認証／認可情報を復元する
    if (!api.basicAuth) {
        const session = getSessionCookie();
        if (session.consoleUserId && session.sessionKey) {
            api.setBasicAuth(session.consoleUserId, session.sessionKey);
            reloadPrivileges(session);
        }
    }

    // 権限に応じて必要なRouterのみツリーに加える
    const elements = [] as JSX.Element[];
    elements.push(<AuthRoutes key="auth" {...props} />);
    if (isLoggedIn()) {
        elements.push(<ManualRoutes key="manual" {...props} />);

        const permissions = getSessionCookie().consoleUserPermissions;
        if (permissions.includes("monitoring")) {
            elements.push(<WeeklyRoutes key="weekly" {...props} />);
            elements.push(<MonitoringRoutes key="monitoring" {...props} />);
            elements.push(<SharedRoutes key="shared" {...props} />);
        }

        if (permissions.includes("reserve")) {
            elements.push(<ReserveRoutes key="reserve" {...props} />);
        }

        if (permissions.includes("chargeable")) {
            elements.push(<UsageBasedRoutes key="usingbased" {...props} />);

            // 二重登録防止
            if (!permissions.includes("monitoring")) {
                elements.push(<SharedRoutes key="monitoring" {...props} />);
            }
        }
    }
    return (
        <BrowserRouter>
            <Redirector {...props} />
            <Container>{elements}</Container>
        </BrowserRouter>
    );
};

async function reloadPrivileges(session: Session) {
    const privileges = await api.getAuthenticationApi().getPrivileges({
        privilegesRequest: { consoleUserId: session.consoleUserId },
    });
    await setupStore(session, privileges.browsableOffices);
}

const Redirector: React.FC<RootState> = (props: RootState) => {
    // 未ログインの場合、OTPありなしを直接指定してたらそちらを使い
    // 未指定（ / など）の場合は直前のログイン成功のpathを使う。
    if (!isLoggedIn()) {
        const redirect = props.common.redirect;
        if (redirect === "/login" || redirect === "/login_otp") {
            return <Redirect to={redirect} />;
        }

        let path = window.location.pathname;
        if (path === "/login" || path === "/login_otp") {
            return <></>;
        }

        const pathname = isLastLoginOtp() ? "/login_otp" : "/login";
        return (
            <Redirect
                to={{
                    pathname: pathname,
                    state: { from: path },
                }}
            />
        );
    }

    const location = useLocation();
    React.useEffect(() => {
        if (location.pathname == props.common.redirect) {
            store.dispatch(commonSlice.actions.redirectDone());
        }
    }, [location, props.common.redirect]);

    if (!props.common.redirect) {
        return <></>;
    }
    if (location.pathname == props.common.redirect) {
        return <></>;
    }
    return <Redirect to={props.common.redirect} />;
};

const AuthRoutes: React.FC<RootState> = (props: RootState) => {
    return (
        <Switch>
            <Route path="/login">
                <LoginContainer form={props.login} common={props.common} />
            </Route>
            <Route path="/login_otp">
                <LoginOTPContainer form={props.login} common={props.common} />
            </Route>
        </Switch>
    );
};

const MonitoringRoutes: React.FC<RootState> = (props: RootState) => {
    return (
        <Switch>
            <Route path="/driver_trips">
                <TripsScreen
                    tripstate={props.trips}
                    loading={props.common.loading}
                />
            </Route>
            <Route path="/driver_breaks">
                <BreaksScreen breakstate={props.breaks} />
            </Route>
            <Route path="/shift_monitoring">
                <SearchScreen
                    commonState={props.common}
                    shifts={props.shifts}
                />
            </Route>
            <Route path="/shift_search">
                <SearchScreen
                    commonState={props.common}
                    shifts={props.shifts}
                />
            </Route>
        </Switch>
    );
};

const ReserveRoutes: React.FC<RootState> = (props: RootState) => {
    return (
        <Switch>
            <Route path="/reserve/shifts">
                <ReserveShiftsContainer
                    commonState={props.common}
                    reserveShifts={props.reserveShifts}
                />
            </Route>
            <Route path="/reserve/trips">
                <ReserveTripsContainer
                    tripstate={props.reserveTrips}
                    loading={props.common.loading}
                />
            </Route>
            <Route path="/reserve/trip/map/:loginSessionId/:unladenTripSessionId/:unladenStartTs">
                <ReserveTripsMap />
            </Route>
        </Switch>
    );
};

const WeeklyRoutes: React.FC<RootState> = (props: RootState) => {
    return (
        <Switch>
            <Route path="/weekly_report">
                <WeeklyReportsContainer
                    common={props.common}
                    weeklyreport={props.weeklyReports}
                    weeklydetail={props.detailScreen}
                />
            </Route>
            <Route path="/weekly_driver_shift">
                <WeeklyDetailScreenContainer
                    common={props.common}
                    weeklyreport={props.weeklyReports}
                    weeklydetail={props.detailScreen}
                />
            </Route>
        </Switch>
    );
};

const UsageBasedRoutes: React.FC<RootState> = (props: RootState) => {
    return (
        <Switch>
            <Route path="/payments">
                <BasePaymentsRouter
                    state={props.payments}
                    common={props.common}
                />
            </Route>
            <Route path="/driver_paid_trips">
                <BillingSummaryByDriverContainer
                    state={props.payments}
                    common={props.common}
                />
            </Route>
            <Route path="/driver_usage">
                <BilledTripsDetailsContainer
                    state={props.usage}
                    common={props.common}
                />
            </Route>
            <Route exact path="/capacity">
                <CapacityContainer
                    state={props.capacity}
                    common={props.common}
                />
            </Route>
        </Switch>
    );
};

// Orignalと従量課金で共有しているRoute
const SharedRoutes: React.FC<RootState> = (props: RootState) => {
    return (
        <Switch>
            <Route path="/drivers_list_menu">
                <DriversListContainer
                    common={props.common}
                    drivermenu={props.drivermenu}
                    selectedOfficeId={props.shifts.officeId}
                />
            </Route>
            <Route path="/trips_map">
                <TripsMap
                    tripstate={props.trips}
                    selectedTrip={props.common.selectedTrip}
                    permissions={getSessionCookie().consoleUserPermissions}
                    common={props.common}
                    neighborsButtonAvailable={props.common.canUseMapNeighbors}
                />
            </Route>
        </Switch>
    );
};

const ManualRoutes: React.FC<RootState> = (props: RootState) => {
    return (
        <Switch>
            <Route exact path="/release_note">
                <ReleaseNoteContainer
                    state={props.releaseNote}
                    common={props.common}
                />
            </Route>
            <Route exact path="/manual">
                <ManualContainer state={props.manual} common={props.common} />
            </Route>
        </Switch>
    );
};
export const Router = ReactRedux.connect((state: RootState) => state)(
    BaseRouter
);
